import React from 'react';

/**
 * Flat vector illustrations for the marketing pages.
 *
 * Every fill and stroke is a design token, so the art follows the theme
 * exactly like the rest of the UI — no second palette, no baked-in colour,
 * nothing to re-export when a token moves.
 *
 * Two hues per scene: `--accent` and `--accent-alt`, both supplied by the
 * .accent-N / .spectrum-N class on the container. Drop an illustration inside
 * `.accent-4` and it becomes the green one. The neutral plane is
 * --illus-paper / --illus-line, which stays visible on any surface.
 *
 * All of it is decoration — the copy beside it carries the meaning — so every
 * SVG is aria-hidden and unfocusable.
 */

const A       = 'var(--accent, var(--color-primary))';
const ALT     = 'var(--accent-alt, var(--color-accent-violet))';
const PAPER   = 'var(--illus-paper)';
const LINE    = 'var(--illus-line)';
const SURFACE = 'var(--color-surface)';
const INK     = 'var(--color-text)';
const MUTED   = 'var(--color-text-muted)';
const OK      = 'var(--color-success)';

const Art = ({ children, className = '', viewBox = '0 0 240 180' }) => (
  <svg
    viewBox={viewBox}
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/* A soft accent bloom behind the scene — the same idea as .orb, inline. */
const Bloom = ({ cx, cy, r, fill = A, opacity = 0.12 }) => (
  <circle cx={cx} cy={cy} r={r} fill={fill} opacity={opacity} />
);

/* ─────────────────────────────────────────────────────────────
   Auto dialer — a phone working a queue, calls radiating out
   ───────────────────────────────────────────────────────────── */
export const AutoDialerArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="62" cy="70" r="66" />
    <Bloom cx="186" cy="120" r="44" fill={ALT} opacity={0.1} />

    {/* Radiating call arcs */}
    {[0, 1, 2].map(i => (
      <path
        key={i}
        d={`M118 ${90 - (i + 1) * 6} a${26 + i * 18} ${26 + i * 18} 0 0 1 0 ${(26 + i * 18) * 2}`}
        stroke={A}
        strokeWidth="3"
        strokeLinecap="round"
        opacity={0.5 - i * 0.14}
      />
    ))}

    {/* Phone */}
    <rect x="26" y="20" width="82" height="140" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />
    <rect x="34" y="34" width="66" height="112" rx="10" fill={SURFACE} />
    <rect x="56" y="26" width="22" height="4" rx="2" fill={LINE} />

    {/* Active contact */}
    <circle cx="67" cy="62" r="14" fill={A} />
    <path d="M62 62a6 6 0 0 1 10 0" stroke={SURFACE} strokeWidth="2" strokeLinecap="round" />
    <circle cx="67" cy="56" r="3.4" fill={SURFACE} />
    <rect x="47" y="84" width="40" height="5" rx="2.5" fill={INK} opacity="0.24" />
    <rect x="53" y="94" width="28" height="4" rx="2" fill={INK} opacity="0.14" />

    {/* Call control */}
    <circle cx="67" cy="124" r="13" fill={A} />
    <path
      d="M62.5 120.5c0 4.5 3.5 8 8 8l1.5-3-3.5-2-1.5 1.5a7 7 0 0 1-2-2l1.5-1.5-2-3.5-2 1.5Z"
      fill={SURFACE}
    />

    {/* Queue cards */}
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(146 ${34 + i * 44})`}>
        <rect width="76" height="34" rx="10" fill={SURFACE} stroke={LINE} strokeWidth="2" />
        <circle cx="18" cy="17" r="8" fill={i === 0 ? ALT : LINE} opacity={i === 0 ? 1 : 0.7} />
        <rect x="33" y="11" width="30" height="4.5" rx="2.25" fill={INK} opacity="0.2" />
        <rect x="33" y="20" width="20" height="4" rx="2" fill={INK} opacity="0.12" />
      </g>
    ))}

    {/* Done tick on the first queue card */}
    <circle cx="214" cy="38" r="9" fill={OK} />
    <path d="M210 38.5l3 3 5.5-6" stroke={SURFACE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Lead management — a pipeline board
   ───────────────────────────────────────────────────────────── */
export const LeadsArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="70" cy="52" r="58" />
    <Bloom cx="180" cy="140" r="48" fill={ALT} opacity={0.1} />

    <rect x="18" y="24" width="204" height="134" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />

    {[0, 1, 2].map(col => (
      <g key={col} transform={`translate(${30 + col * 64} 38)`}>
        <rect width="52" height="6" rx="3" fill={col === 0 ? A : col === 1 ? ALT : LINE} />
        {[0, 1, 2].slice(0, 3 - col).map(row => (
          <g key={row} transform={`translate(0 ${16 + row * 34})`}>
            <rect width="52" height="28" rx="8" fill={SURFACE} stroke={LINE} strokeWidth="1.5" />
            <circle cx="12" cy="14" r="6" fill={col === 0 ? A : col === 1 ? ALT : LINE} opacity={row === 0 ? 1 : 0.45} />
            <rect x="23" y="9" width="21" height="4" rx="2" fill={INK} opacity="0.2" />
            <rect x="23" y="17" width="14" height="3.5" rx="1.75" fill={INK} opacity="0.12" />
          </g>
        ))}
      </g>
    ))}

    {/* The card being dragged across */}
    <g transform="translate(104 118) rotate(-6)">
      <rect x="1" y="3" width="52" height="28" rx="8" fill="var(--illus-shade)" />
      <rect width="52" height="28" rx="8" fill={SURFACE} stroke={A} strokeWidth="2" />
      <circle cx="12" cy="14" r="6" fill={A} />
      <rect x="23" y="9" width="21" height="4" rx="2" fill={INK} opacity="0.24" />
      <rect x="23" y="17" width="14" height="3.5" rx="1.75" fill={INK} opacity="0.14" />
    </g>
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Analytics — bars, a trend line and a donut
   ───────────────────────────────────────────────────────────── */
export const AnalyticsArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="72" cy="60" r="58" />
    <Bloom cx="188" cy="128" r="42" fill={ALT} opacity={0.1} />

    <rect x="18" y="26" width="132" height="128" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />

    {/* Grid lines */}
    {[0, 1, 2].map(i => (
      <line key={i} x1="30" y1={62 + i * 26} x2="138" y2={62 + i * 26} stroke={LINE} strokeWidth="1.5" strokeDasharray="4 3" />
    ))}

    {/* Bars */}
    {[34, 58, 44, 74, 56].map((h, i) => (
      <rect
        key={i}
        x={32 + i * 21}
        y={140 - h}
        width="13"
        height={h}
        rx="5"
        fill={i % 2 === 0 ? A : ALT}
        opacity={i % 2 === 0 ? 0.92 : 0.55}
      />
    ))}
    <line x1="30" y1="141" x2="138" y2="141" stroke={LINE} strokeWidth="2" strokeLinecap="round" />

    {/* Trend */}
    <path d="M38 100 L59 84 L80 92 L101 66 L122 78" stroke={INK} strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    {[[38, 100], [59, 84], [80, 92], [101, 66], [122, 78]].map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="3.4" fill={SURFACE} stroke={INK} strokeOpacity="0.35" strokeWidth="2" />
    ))}

    {/* Donut */}
    <g transform="translate(190 74)">
      <circle r="34" fill={SURFACE} stroke={LINE} strokeWidth="2" />
      <circle r="24" fill="none" stroke={LINE} strokeWidth="9" />
      <circle
        r="24"
        fill="none"
        stroke={A}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray="150.8"
        strokeDashoffset="46"
        transform="rotate(-90)"
      />
      <circle
        r="24"
        fill="none"
        stroke={ALT}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray="150.8"
        strokeDashoffset="122"
        transform="rotate(120)"
      />
    </g>

    {/* Legend */}
    {[0, 1].map(i => (
      <g key={i} transform={`translate(166 ${126 + i * 16})`}>
        <rect width="9" height="9" rx="3" fill={i === 0 ? A : ALT} />
        <rect x="15" y="2.5" width={i === 0 ? 38 : 28} height="4.5" rx="2.25" fill={INK} opacity="0.18" />
      </g>
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Live monitoring — a wall of agents, one on a live call
   ───────────────────────────────────────────────────────────── */
export const MonitoringArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="120" cy="66" r="70" />

    <rect x="16" y="22" width="208" height="112" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />

    {[0, 1, 2, 3, 4, 5].map(i => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      const live = i === 1 || i === 3;
      return (
        <g key={i} transform={`translate(${28 + col * 64} ${34 + row * 50})`}>
          <rect width="52" height="38" rx="10" fill={SURFACE} stroke={live ? A : LINE} strokeWidth={live ? 2 : 1.5} />
          <circle cx="16" cy="19" r="9" fill={live ? A : LINE} opacity={live ? 1 : 0.55} />
          <path d="M11.5 20.5a5 5 0 0 1 9 0" stroke={SURFACE} strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="16" cy="15" r="2.6" fill={SURFACE} />
          <rect x="30" y="12" width="15" height="4" rx="2" fill={INK} opacity="0.2" />
          {live ? (
            <>
              <circle cx="32.5" cy="24.5" r="3" fill={OK} />
              <rect x="38" y="22.5" width="8" height="4" rx="2" fill={OK} opacity="0.4" />
            </>
          ) : (
            <rect x="30" y="22" width="12" height="4" rx="2" fill={INK} opacity="0.1" />
          )}
        </g>
      );
    })}

    {/* Live waveform strip */}
    <rect x="16" y="144" width="208" height="24" rx="12" fill={SURFACE} stroke={LINE} strokeWidth="2" />
    {[6, 12, 8, 16, 10, 18, 7, 13, 9, 15, 6, 11, 8, 14].map((h, i) => (
      <rect
        key={i}
        x={32 + i * 13}
        y={156 - h / 2}
        width="4"
        height={h}
        rx="2"
        fill={i % 3 === 0 ? A : ALT}
        opacity={i % 3 === 0 ? 0.9 : 0.4}
      />
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   No hardware — a rack retired in favour of a phone
   ───────────────────────────────────────────────────────────── */
export const NoHardwareArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="176" cy="86" r="60" />

    {/* The old way — greyed, dashed, on its way out */}
    <g opacity="0.55">
      <rect x="20" y="40" width="72" height="100" rx="12" fill={PAPER} stroke={LINE} strokeWidth="2" strokeDasharray="6 5" />
      {[0, 1, 2, 3].map(i => (
        <g key={i} transform={`translate(30 ${52 + i * 22})`}>
          <rect width="52" height="14" rx="4" fill={SURFACE} stroke={LINE} strokeWidth="1.5" />
          <circle cx="9" cy="7" r="2.6" fill={MUTED} />
          <rect x="17" y="5" width="26" height="4" rx="2" fill={INK} opacity="0.12" />
        </g>
      ))}
    </g>
    <path d="M28 44 L84 136 M84 44 L28 136" stroke={MUTED} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />

    {/* Arrow across */}
    <path d="M100 90h26" stroke={A} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 7" />
    <path d="M126 84l8 6-8 6" stroke={A} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* The new way */}
    <rect x="150" y="30" width="70" height="120" rx="16" fill={PAPER} stroke={A} strokeWidth="2.5" />
    <rect x="158" y="44" width="54" height="92" rx="9" fill={SURFACE} />
    <rect x="174" y="35" width="22" height="4" rx="2" fill={LINE} />
    <circle cx="185" cy="78" r="17" fill={A} opacity="0.16" />
    <circle cx="185" cy="78" r="11" fill={A} />
    <path d="M181 78.5l3 3 5.5-6.5" stroke={SURFACE} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="166" y="104" width="38" height="5" rx="2.5" fill={INK} opacity="0.18" />
    <rect x="174" y="116" width="22" height="4.5" rx="2.25" fill={INK} opacity="0.11" />
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Remote / anywhere — pins on a globe, a phone in hand
   ───────────────────────────────────────────────────────────── */
export const RemoteArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="104" cy="88" r="70" />

    <circle cx="104" cy="88" r="62" fill={PAPER} stroke={LINE} strokeWidth="2" />
    <ellipse cx="104" cy="88" rx="62" ry="24" stroke={LINE} strokeWidth="1.5" />
    <ellipse cx="104" cy="88" rx="30" ry="62" stroke={LINE} strokeWidth="1.5" />
    <line x1="42" y1="88" x2="166" y2="88" stroke={LINE} strokeWidth="1.5" />

    {/* Connection arcs */}
    <path d="M62 62c26 22 60 26 92 8" stroke={A} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 6" />
    <path d="M70 118c22-16 54-14 76 4" stroke={ALT} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3 6" />

    {/* Pins */}
    {[[68, 60, A], [132, 66, ALT], [80, 118, ALT], [140, 116, A]].map(([x, y, c], i) => (
      <g key={i} transform={`translate(${x} ${y})`}>
        <path d="M0-14a10 10 0 0 1 10 10c0 7-10 16-10 16S-10 3-10-4A10 10 0 0 1 0-14Z" fill={c} />
        <circle cy="-4" r="4" fill={SURFACE} />
      </g>
    ))}

    {/* Phone overlay */}
    <g transform="translate(168 84)">
      <rect x="2" y="4" width="48" height="82" rx="12" fill="var(--illus-shade)" />
      <rect width="48" height="82" rx="12" fill={SURFACE} stroke={LINE} strokeWidth="2" />
      <rect x="8" y="14" width="32" height="4.5" rx="2.25" fill={INK} opacity="0.18" />
      <rect x="8" y="25" width="22" height="4" rx="2" fill={INK} opacity="0.11" />
      <circle cx="24" cy="54" r="13" fill={A} />
      <path
        d="M19.5 50.5c0 4.5 3.5 8 8 8l1.5-3-3.5-2-1.5 1.5a7 7 0 0 1-2-2l1.5-1.5-2-3.5-2 1.5Z"
        fill={SURFACE}
      />
    </g>
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Security — shield, lock, role rows
   ───────────────────────────────────────────────────────────── */
export const SecurityArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="96" cy="86" r="62" />

    <path
      d="M96 20l52 20v42c0 34-22 58-52 70-30-12-52-36-52-70V40l52-20Z"
      fill={PAPER}
      stroke={LINE}
      strokeWidth="2"
    />
    <path
      d="M96 32l40 15.5V82c0 27-17 46-40 56-23-10-40-29-40-56V47.5L96 32Z"
      fill={A}
      opacity="0.14"
    />

    {/* Lock */}
    <path d="M82 82v-9a14 14 0 0 1 28 0v9" stroke={A} strokeWidth="5" strokeLinecap="round" />
    <rect x="72" y="82" width="48" height="38" rx="10" fill={A} />
    <circle cx="96" cy="98" r="5.5" fill={SURFACE} />
    <rect x="93.5" y="100" width="5" height="11" rx="2.5" fill={SURFACE} />

    {/* Role rows */}
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(168 ${44 + i * 34})`}>
        <rect width="56" height="26" rx="9" fill={SURFACE} stroke={LINE} strokeWidth="1.5" />
        <circle cx="14" cy="13" r="6" fill={i === 0 ? A : ALT} opacity={i === 0 ? 1 : 0.5} />
        <rect x="26" y="10.5" width="20" height="4.5" rx="2.25" fill={INK} opacity="0.16" />
      </g>
    ))}
    <path d="M148 57h14M148 91h14M148 125h14" stroke={LINE} strokeWidth="2" strokeLinecap="round" strokeDasharray="3 5" />
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Integrations — a hub and its spokes
   ───────────────────────────────────────────────────────────── */
export const IntegrationsArt = ({ className }) => {
  const nodes = [
    [52, 44, A], [188, 44, ALT], [36, 118, ALT],
    [204, 118, A], [120, 156, ALT],
  ];
  return (
    <Art className={className}>
      <Bloom cx="120" cy="88" r="64" />
      {nodes.map(([x, y], i) => (
        <line key={i} x1="120" y1="88" x2={x} y2={y} stroke={LINE} strokeWidth="2" strokeDasharray="4 5" />
      ))}

      {nodes.map(([x, y, c], i) => (
        <g key={i} transform={`translate(${x - 20} ${y - 20})`}>
          <rect width="40" height="40" rx="13" fill={SURFACE} stroke={LINE} strokeWidth="2" />
          <rect x="11" y="11" width="18" height="18" rx="6" fill={c} opacity={i % 2 ? 0.55 : 0.9} />
        </g>
      ))}

      <g transform="translate(84 52)">
        <rect x="2" y="4" width="72" height="72" rx="24" fill="var(--illus-shade)" />
        <rect width="72" height="72" rx="24" fill={A} />
        <path d="M28 36h16M36 28v16" stroke={SURFACE} strokeWidth="4.5" strokeLinecap="round" />
      </g>
    </Art>
  );
};

/* ─────────────────────────────────────────────────────────────
   Channels — the WASLX mark wired to the places calls and leads
   actually come from.

   Same hub-and-spoke geometry as IntegrationsArt, but the generic
   coloured squares are replaced by the real marks: WhatsApp, Facebook,
   Instagram, Android and SIP. The point of the picture on /use-cases is
   "one platform, every channel", and five anonymous rounded squares
   said "one platform, five somethings".

   Brand hexes are hard-coded on purpose — these are other companies'
   marks, and a WhatsApp glyph tinted with our accent is not a WhatsApp
   glyph. The tiles stay white so the marks read on the dark hero panel.
   ───────────────────────────────────────────────────────────── */
export const ChannelsArt = ({ className }) => {
  const nodes = [
    { x: 52,  y: 44,  brand: '#25D366', key: 'whatsapp' },
    { x: 188, y: 44,  brand: '#1877F2', key: 'facebook' },
    { x: 36,  y: 118, brand: '#E4405F', key: 'instagram' },
    { x: 204, y: 118, brand: '#3DDC84', key: 'android' },
    { x: 120, y: 156, brand: '#0EA5E9', key: 'sip' },
  ];

  const glyph = (key, brand) => {
    switch (key) {
      case 'whatsapp':
        return (
          <g>
            <circle cx="20" cy="20" r="11" fill={brand} />
            <path
              d="M15.6 15.4c.3-.7.6-.7.9-.7h.7c.2 0 .5 0 .7.6l.9 2.1c.1.3 0 .5-.1.7l-.5.6c-.2.2-.3.4-.1.7a7 7 0 003.4 3c.3.1.5.1.7-.1l.7-.8c.2-.2.4-.2.6-.1l2 1c.3.1.4.3.4.5 0 .8-.5 1.7-1 2-.5.3-1.4.5-2.4.2a11 11 0 01-6.3-5.5c-.6-1.3-.7-2.5-.6-3.2.1-.5.2-.8.4-1z"
              fill="#fff"
            />
          </g>
        );
      case 'facebook':
        return (
          <g>
            <rect x="9" y="9" width="22" height="22" rx="7" fill={brand} />
            <path
              d="M22.4 20.4h-2.1v7.4h-3.1v-7.4h-1.5v-2.6h1.5v-1.7c0-1.9 1-2.9 3-2.9h2.2v2.6h-1.5c-.4 0-.6.2-.6.6v1.4h2.3l-.2 2.6z"
              fill="#fff"
            />
          </g>
        );
      case 'instagram':
        return (
          <g>
            <rect x="9" y="9" width="22" height="22" rx="7" fill={brand} />
            <rect x="13.5" y="13.5" width="13" height="13" rx="4.4" fill="none" stroke="#fff" strokeWidth="1.9" />
            <circle cx="20" cy="20" r="3.3" fill="none" stroke="#fff" strokeWidth="1.9" />
            <circle cx="24.6" cy="15.4" r="1.15" fill="#fff" />
          </g>
        );
      case 'android':
        return (
          <g fill={brand}>
            <path d="M13.2 19.6h13.6v6.2a1.5 1.5 0 01-1.5 1.5H14.7a1.5 1.5 0 01-1.5-1.5v-6.2z" />
            <path
              d="M13.4 18.2a6.7 6.7 0 0113.2 0H13.4z"
            />
            <circle cx="17.2" cy="15.8" r="0.85" fill="#fff" />
            <circle cx="22.8" cy="15.8" r="0.85" fill="#fff" />
            <path d="M14.6 11.6l1.3 2M25.4 11.6l-1.3 2" stroke={brand} strokeWidth="1.4" strokeLinecap="round" fill="none" />
            <rect x="9.6" y="19.4" width="2.6" height="6.4" rx="1.3" />
            <rect x="27.8" y="19.4" width="2.6" height="6.4" rx="1.3" />
          </g>
        );
      default:
        return (
          <g>
            <circle cx="20" cy="20" r="11" fill={brand} />
            <path
              d="M16.2 15.6c.4-.4 1-.4 1.3 0l1.3 1.5c.3.3.3.8 0 1.1l-.8.9a6 6 0 002.9 2.9l.9-.8c.3-.3.8-.3 1.1 0l1.5 1.3c.4.3.4.9 0 1.3l-.7.7c-.5.5-1.2.6-1.8.4a11 11 0 01-6.5-6.5c-.2-.6 0-1.3.4-1.8l.4-.4z"
              fill="#fff"
            />
          </g>
        );
    }
  };

  return (
    <Art className={className}>
      <Bloom cx="120" cy="88" r="64" />

      {nodes.map(n => (
        <line
          key={`l-${n.key}`}
          x1="120" y1="88" x2={n.x} y2={n.y}
          stroke={LINE} strokeWidth="2" strokeDasharray="4 5"
        />
      ))}

      {nodes.map(n => (
        <g key={n.key} transform={`translate(${n.x - 20} ${n.y - 20})`}>
          <rect width="40" height="40" rx="13" fill={SURFACE} stroke={LINE} strokeWidth="2" />
          {glyph(n.key, n.brand)}
        </g>
      ))}

      {/* The hub: the WASLX mark on its own white tile, so the logo is
          read as the platform everything else plugs into. */}
      <g transform="translate(84 52)">
        <rect x="2" y="4" width="72" height="72" rx="24" fill="var(--illus-shade)" />
        <rect width="72" height="72" rx="24" fill={SURFACE} stroke={LINE} strokeWidth="2" />
        {/* logo.png now ships in the brand blue (#266dd6), so it reads on this
            white tile unaided. It used to be a white mark that needed
            brightness(0) to be visible at all — that filter would flatten the
            brand colour back to black, so it is deliberately gone. */}
        <image
          href="/logo.png"
          x="11"
          y="17"
          width="50"
          height="38"
          preserveAspectRatio="xMidYMid meet"
        />
      </g>
    </Art>
  );
};

/* ─────────────────────────────────────────────────────────────
   WhatsApp / conversation — bubbles and delivery ticks
   ───────────────────────────────────────────────────────────── */
export const ConversationArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="104" cy="76" r="64" />

    {/* Incoming */}
    <g transform="translate(20 34)">
      <rect width="118" height="46" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <path d="M14 46v10l14-10Z" fill={PAPER} stroke={LINE} strokeWidth="2" strokeLinejoin="round" />
      <rect x="18" y="15" width="66" height="5" rx="2.5" fill={INK} opacity="0.2" />
      <rect x="18" y="27" width="44" height="4.5" rx="2.25" fill={INK} opacity="0.12" />
    </g>

    {/* Outgoing */}
    <g transform="translate(94 96)">
      <rect x="2" y="4" width="126" height="50" rx="16" fill="var(--illus-shade)" />
      <rect width="126" height="50" rx="16" fill={A} />
      <path d="M112 50v11l-14-11Z" fill={A} />
      <rect x="18" y="16" width="74" height="5" rx="2.5" fill={SURFACE} opacity="0.85" />
      <rect x="18" y="29" width="50" height="4.5" rx="2.25" fill={SURFACE} opacity="0.55" />
      <path d="M92 36l3.5 3.5 7-7.5M100 36l3.5 3.5 7-7.5" stroke={SURFACE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
    </g>

    {/* Typing dots */}
    <g transform="translate(24 96)">
      <rect width="58" height="34" rx="14" fill={SURFACE} stroke={LINE} strokeWidth="2" />
      {[0, 1, 2].map(i => (
        <circle key={i} cx={17 + i * 12} cy="17" r="4" fill={ALT} opacity={0.4 + i * 0.25} />
      ))}
    </g>
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Campaigns — a megaphone reaching a list
   ───────────────────────────────────────────────────────────── */
export const CampaignArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="82" cy="80" r="60" />

    <g transform="translate(30 48)">
      <path d="M0 30a12 12 0 0 1 12-12h20L82 0v84L32 66H12A12 12 0 0 1 0 54Z" fill={A} />
      <path d="M32 18v48" stroke={SURFACE} strokeWidth="2.5" opacity="0.5" />
      <rect x="16" y="66" width="18" height="30" rx="8" fill={A} opacity="0.55" />
    </g>

    {/* Broadcast waves */}
    {[0, 1, 2].map(i => (
      <path
        key={i}
        d={`M120 ${74 - i * 9} a${18 + i * 14} ${18 + i * 14} 0 0 1 0 ${(18 + i * 14) * 2}`}
        stroke={ALT}
        strokeWidth="3"
        strokeLinecap="round"
        opacity={0.55 - i * 0.15}
      />
    ))}

    {/* Reached contacts */}
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(178 ${40 + i * 40})`}>
        <rect width="46" height="30" rx="10" fill={SURFACE} stroke={LINE} strokeWidth="2" />
        <circle cx="15" cy="15" r="7" fill={i === 1 ? ALT : LINE} opacity={i === 1 ? 1 : 0.6} />
        <rect x="27" y="12" width="12" height="4" rx="2" fill={INK} opacity="0.16" />
      </g>
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Team — agents onboarded fast
   ───────────────────────────────────────────────────────────── */
export const TeamArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="120" cy="82" r="66" />

    <rect x="26" y="112" width="188" height="46" rx="18" fill={PAPER} stroke={LINE} strokeWidth="2" />

    {[[74, A, 1], [120, ALT, 1.16], [166, A, 1]].map(([x, c, s], i) => (
      <g key={i} transform={`translate(${x} ${96 - (s - 1) * 40}) scale(${s})`}>
        <path d="M-26 34a26 26 0 0 1 52 0Z" fill={c} opacity={i === 1 ? 1 : 0.55} />
        <circle cy="4" r="17" fill={c} opacity={i === 1 ? 1 : 0.55} />
        <circle cy="4" r="17" fill={SURFACE} opacity="0.12" />
      </g>
    ))}

    {/* Onboarding ticks */}
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(${58 + i * 46} 128)`}>
        <circle cx="10" cy="10" r="10" fill={OK} opacity={i === 2 ? 0.35 : 1} />
        <path d="M5.5 10.5l3 3 6-6.5" stroke={SURFACE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Support — headset and a help thread
   ───────────────────────────────────────────────────────────── */
export const SupportArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="96" cy="84" r="60" />

    <path d="M50 96V84a46 46 0 0 1 92 0v12" stroke={A} strokeWidth="6" strokeLinecap="round" />
    <rect x="34" y="90" width="26" height="42" rx="12" fill={A} />
    <rect x="132" y="90" width="26" height="42" rx="12" fill={A} />
    <path d="M132 128v6a14 14 0 0 1-14 14h-14" stroke={A} strokeWidth="5" strokeLinecap="round" />
    <rect x="86" y="138" width="26" height="20" rx="9" fill={SURFACE} stroke={A} strokeWidth="2.5" />

    {[0, 1].map(i => (
      <g key={i} transform={`translate(172 ${52 + i * 46})`}>
        <rect width="52" height="34" rx="12" fill={SURFACE} stroke={LINE} strokeWidth="2" />
        <rect x="12" y="11" width="28" height="4.5" rx="2.25" fill={INK} opacity="0.18" />
        <rect x="12" y="21" width="18" height="4" rx="2" fill={INK} opacity="0.11" />
        <circle cx="46" cy="4" r="5" fill={i === 0 ? ALT : OK} />
      </g>
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   API / docs — a code window
   ───────────────────────────────────────────────────────────── */
export const ApiArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="120" cy="82" r="66" />

    <rect x="24" y="28" width="192" height="124" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />
    <path d="M24 52h192" stroke={LINE} strokeWidth="2" />
    {[0, 1, 2].map(i => (
      <circle key={i} cx={42 + i * 14} cy="40" r="4" fill={i === 0 ? A : LINE} opacity={i === 0 ? 1 : 0.6} />
    ))}

    {[
      [40, 30, A], [76, 46, null], [40, 22, ALT], [66, 38, null],
      [52, 34, ALT], [40, 26, null],
    ].map(([x, w, c], i) => (
      <g key={i} transform={`translate(${x} ${68 + i * 14})`}>
        <rect width={w} height="6" rx="3" fill={c || INK} opacity={c ? 0.85 : 0.16} />
        {i % 2 === 0 && <rect x={w + 8} y="0" width={w * 0.6} height="6" rx="3" fill={INK} opacity="0.1" />}
      </g>
    ))}

    <path d="M180 96l12 12-12 12" stroke={A} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M164 120l8-32" stroke={ALT} strokeWidth="3" strokeLinecap="round" />
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Setup speed — a clock and a launch checklist
   ───────────────────────────────────────────────────────────── */
export const SetupArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="86" cy="84" r="60" />

    <circle cx="86" cy="88" r="54" fill={PAPER} stroke={LINE} strokeWidth="2" />
    <circle cx="86" cy="88" r="42" fill={SURFACE} />
    <circle
      cx="86"
      cy="88"
      r="48"
      fill="none"
      stroke={A}
      strokeWidth="6"
      strokeLinecap="round"
      strokeDasharray="301.6"
      strokeDashoffset="226"
      transform="rotate(-90 86 88)"
    />
    <path d="M86 62v26l18 11" stroke={INK} strokeOpacity="0.55" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="86" cy="88" r="4" fill={A} />

    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(158 ${48 + i * 38})`}>
        <rect width="66" height="28" rx="10" fill={SURFACE} stroke={LINE} strokeWidth="2" />
        <circle cx="15" cy="14" r="7" fill={i < 2 ? OK : LINE} opacity={i < 2 ? 1 : 0.6} />
        {i < 2 && <path d="M11.5 14.5l2.5 2.5 5-5.5" stroke={SURFACE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
        <rect x="29" y="11.5" width="26" height="5" rx="2.5" fill={INK} opacity={i < 2 ? 0.2 : 0.1} />
      </g>
    ))}
  </Art>
);

/* ─────────────────────────────────────────────────────────────
   Savings — cost falling away
   ───────────────────────────────────────────────────────────── */
export const SavingsArt = ({ className }) => (
  <Art className={className}>
    <Bloom cx="120" cy="88" r="66" />

    <rect x="22" y="30" width="196" height="124" rx="16" fill={PAPER} stroke={LINE} strokeWidth="2" />

    {[92, 70, 52, 30].map((h, i) => (
      <g key={i}>
        <rect x={40 + i * 46} y={140 - h} width="28" height={h} rx="9" fill={i === 3 ? A : LINE} opacity={i === 3 ? 1 : 0.55} />
        {i === 3 && <rect x={40 + i * 46} y={140 - h} width="28" height={h} rx="9" fill={ALT} opacity="0.25" />}
      </g>
    ))}

    <path d="M54 44 L100 66 L146 84 L192 104" stroke={A} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="5 6" />
    <path d="M192 104l-3-11 11 3" stroke={A} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    <g transform="translate(146 34)">
      <rect width="60" height="26" rx="13" fill={SURFACE} stroke={LINE} strokeWidth="2" />
      <path d="M16 13h28" stroke={OK} strokeWidth="3" strokeLinecap="round" />
      <path d="M38 8l6 5-6 5" stroke={OK} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  </Art>
);

export default {
  AutoDialerArt,
  LeadsArt,
  AnalyticsArt,
  MonitoringArt,
  NoHardwareArt,
  RemoteArt,
  SecurityArt,
  IntegrationsArt,
  ConversationArt,
  CampaignArt,
  TeamArt,
  SupportArt,
  ApiArt,
  SetupArt,
  SavingsArt,
};
